<template>
  <div class="rt">
    <header class="rt-head">
      <div class="rt-title">
        <svg
          width="18"
          height="18"
          viewBox="0 0 24 24"
          fill="currentColor"
          class="rt-play"
        >
          <polygon points="5 3 19 12 5 21 5 3" />
        </svg>
        Recording
      </div>
      <div class="rt-meta">
        <span v-if="lengthLabel">{{ lengthLabel }}</span>
        <span v-if="lengthLabel && recording.size_readable" class="rt-dot"
          >·</span
        >
        <span v-if="recording.size_readable">{{
          recording.size_readable
        }}</span>
        <span
          v-if="
            recording.participants && (lengthLabel || recording.size_readable)
          "
          class="rt-dot"
          >·</span
        >
        <span v-if="recording.participants">
          {{ recording.participants }}
          {{
            String(recording.participants) === '1'
              ? 'participant'
              : 'participants'
          }}
        </span>
      </div>
    </header>

    <div
      v-if="recording.state === 'processing' || !videoUrl"
      class="rt-processing"
    >
      <div class="rt-spinner"></div>
      <p>Recording is still processing. Refresh in a few minutes.</p>
    </div>

    <div v-else-if="videoFailed" class="rt-fallback">
      <p>The video is still being compiled. Refresh in a few minutes.</p>
      <a
        v-if="playerUrl"
        :href="playerUrl"
        target="_blank"
        rel="noopener"
        class="rt-watch"
      >
        Open in the player
      </a>
    </div>

    <template v-else>
      <div class="rt-frame-wrap">
        <video
          class="rt-video"
          controls
          preload="metadata"
          playsinline
          @error="videoFailed = true"
        >
          <source :src="videoUrl" type="video/mp4" />
        </video>
      </div>
      <div class="rt-actions">
        <a
          v-if="playerUrl"
          :href="playerUrl"
          target="_blank"
          rel="noopener"
          class="rt-watch"
        >
          Open in the player
          <svg
            width="12"
            height="12"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2.2"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <path d="M7 17L17 7" />
            <polyline points="7 7 17 7 17 17" />
          </svg>
        </a>
        <button type="button" class="rt-copy" @click="copyLink">
          {{ copied ? 'Link copied' : 'Copy video link' }}
        </button>
      </div>
    </template>

    <footer v-if="recording.start_time || recording.end_time" class="rt-times">
      <span v-if="recording.start_time">{{ recording.start_time }}</span>
      <span v-if="recording.start_time && recording.end_time" class="rt-arrow"
        >→</span
      >
      <span v-if="recording.end_time">{{ recording.end_time }}</span>
    </footer>
  </div>
</template>

<script>
export default {
  name: 'RecordingTab',
  props: {
    recording: { type: Object, required: true },
    // Carried to the player so the transcript can name speakers (see RecordingPlay).
    publicMeetingId: { type: String, default: null },
  },
  data() {
    return {
      videoFailed: false,
      copied: false,
    };
  },
  computed: {
    // The compiled MP4 lives next to the BBB presentation playback on the same
    // host: /playback/presentation/2.3/<id> -> /playback/video/<id>/video-0.m4v.
    // This is the file the /play-recording page uses, not the slide player.
    videoUrl() {
      const r = this.recording || {};
      if (
        r.playback_url &&
        r.playback_url.includes('/playback/presentation/')
      ) {
        return (
          r.playback_url.replace(
            /\/playback\/presentation\/[^/]+\//,
            '/playback/video/'
          ) + '/video-0.m4v'
        );
      }
      const domain = this.$globals && this.$globals.roomDomain;
      if (r.record_id && domain)
        return `${domain}/playback/video/${r.record_id}/video-0.m4v`;
      return null;
    },
    playerUrl() {
      const id = this.recording && this.recording.record_id;
      if (!id) return null;
      const route = { name: 'RecordingPlay', params: { recordingId: id } };
      if (this.publicMeetingId) route.query = { pub: this.publicMeetingId };
      return this.$router.resolve(route).href;
    },
    lengthLabel() {
      const s = this.recording && this.recording.length_seconds;
      if (!s || s <= 0) return '';
      const m = Math.floor(s / 60);
      const sec = s % 60;
      return m > 0 ? `${m}m ${sec}s` : `${sec}s`;
    },
  },
  watch: {
    videoUrl() {
      this.videoFailed = false;
    },
  },
  methods: {
    async copyLink() {
      try {
        await navigator.clipboard.writeText(this.videoUrl);
        this.copied = true;
        setTimeout(() => {
          this.copied = false;
        }, 2000);
      } catch (e) {
        // clipboard blocked; nothing to recover
      }
    },
  },
};
</script>

<style scoped>
.rt {
  font-family: 'JetBrains Mono', ui-monospace, monospace;
  color: #fff;
}
.rt-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
  margin-bottom: 14px;
}
.rt-title {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 16px;
  font-weight: 700;
}
.rt-play {
  color: #d3ca57;
}
.rt-meta {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: rgba(255, 255, 255, 0.55);
}
.rt-dot {
  color: rgba(255, 255, 255, 0.3);
}

.rt-frame-wrap {
  position: relative;
  width: 100%;
  background: #000;
  border-radius: 10px;
  overflow: hidden;
  border: 1px solid rgba(255, 255, 255, 0.06);
  aspect-ratio: 16 / 9;
}
.rt-video {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  background: #000;
}
.rt-actions {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px;
  margin-top: 12px;
}
.rt-copy {
  padding: 9px 14px;
  background: transparent;
  color: rgba(255, 255, 255, 0.7);
  border: 1px solid rgba(255, 255, 255, 0.14);
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: border-color 0.15s ease, color 0.15s ease;
}
.rt-copy:hover {
  color: #fff;
  border-color: rgba(255, 255, 255, 0.35);
}

.rt-processing,
.rt-fallback {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 3rem 1.5rem;
  background: rgba(255, 255, 255, 0.02);
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 10px;
  gap: 12px;
  color: rgba(255, 255, 255, 0.55);
  font-size: 14px;
}
.rt-spinner {
  width: 32px;
  height: 32px;
  border: 3px solid rgba(211, 202, 87, 0.18);
  border-top-color: #d3ca57;
  border-radius: 50%;
  animation: rt-spin 0.9s linear infinite;
}
@keyframes rt-spin {
  to {
    transform: rotate(360deg);
  }
}
.rt-watch {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 9px 18px;
  background: #d3ca57;
  color: #0b0c14;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 700;
  text-decoration: none;
  transition: background 0.15s ease;
}
.rt-watch:hover {
  background: #ddd56a;
}

.rt-times {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 10px;
  font-size: 11px;
  color: rgba(255, 255, 255, 0.4);
}
.rt-arrow {
  color: rgba(255, 255, 255, 0.25);
}
</style>
